import React, {createContext, use, useContext, useEffect} from 'react';
import {fireAlert} from "../services/alert";
import {PlansApi} from "../api/plans";
import {Plan, Ticket} from "../types/Plans";
import {toBrl} from "../services/functions";

// Fluxo novo aceita cartao (sincrono — cobra no fim do trial) e PIX (assincrono
// — gera QR no fim do trial pra cliente pagar pelo painel). Boleto fora do escopo.
type PaymentMethod = 'credit_card' | 'pix';

interface PlansProps {
    plans: Plan[];
    paymentMethod: PaymentMethod;
    setPaymentMethod: React.Dispatch<React.SetStateAction<PaymentMethod>>;
    selectedPlan: Plan | null;
    setSelectedPlan: React.Dispatch<React.SetStateAction<Plan | null>>;
    installmentsOptions: { value: string; label: string }[];
    selectedInstallment: number | null;
    setSelectedInstallment: React.Dispatch<React.SetStateAction<number | null>>;
    discountTicket: Ticket | null;
    setDiscountTicket: React.Dispatch<React.SetStateAction<Ticket | null>>;
    applyTicket: (value: string) => Promise<void>;
    loadingTicket: boolean;
    allowPix: boolean;
    reativa: boolean;
    comercial: boolean;
}

const urlParams = new URLSearchParams(window.location.search);
const idPlano = urlParams.get('id_plano');
// Reativação (?reativa=1): ação comercial p/ clientes inativos. Mostra só planos anuais,
// aplica o cupom REATIVA40 automaticamente e usa o valor cheio como base (o desconto vem
// só do cupom). Força cartão. Ver reativacao/planejamento.md.
const reativa = urlParams.get('reativa') === '1';
const cupom = urlParams.get('cupom') || (reativa ? 'REATIVA40' : null);
// Link especial pra liberar PIX no checkout (?pix=true), enviado a clientes
// específicos. Checkout público é só cartão. (Mesmo padrão do antigo ?boleto=true.)
// Na reativação forçamos cartão (cobrança automática no dia seguinte).
const allowPix = urlParams.get('pix') === 'true' && !reativa;
// Link comercial (utm_medium=comercial): trava o seletor de plano na 2ª etapa
const comercial = urlParams.get('utm_medium') === 'comercial';

const PlansContext = createContext<PlansProps | undefined>(undefined);

export const PlansProvider: React.FC<React.PropsWithChildren<{}>> = ({children}) => {


    const {getPlans, getTicket} = PlansApi();
    const [paymentMethod, setPaymentMethod] = React.useState<PaymentMethod>('credit_card');
    const [plans, setPlans] = React.useState<Plan[]>([]);
    const [selectedPlan, setSelectedPlan] = React.useState<Plan | null>(null);
    const [installmentsOptions, setInstallmentsOptions] = React.useState<{ value: string; label: string }[]>([]);
    const [selectedInstallment, setSelectedInstallment] = React.useState<number | null>(1);
    const [discountTicket, setDiscountTicket] = React.useState<Ticket | null>(null);
    const [loadingTicket, setLoadingTicket] = React.useState<boolean>(false);
    const fetchPlans = async () => {
        try {
            const response = await getPlans(idPlano ? Number(idPlano) : undefined);
            if (response) {
                // Reativação: só anuais (id_periodicidade 4) e base = valor cheio (ignora o
                // desconto anual; o REATIVA40 incide sobre o cheio). Com valor === valor_cheio,
                // toda a exibição/cálculo do checkout já resulta correto sem outras mudanças.
                const list = reativa
                    ? response
                        .filter(plan => plan.id_periodicidade === 4)
                        .map(plan => ({ ...plan, valor: plan.valor_cheio }))
                    : response;
                setPlans(list);
                if (!selectedPlan) {
                    if (idPlano) {
                        const plan = list.find(plan => plan.id_superlogica === Number(idPlano));
                        if (plan) {
                            setSelectedPlan(plan);
                        }
                        return;
                    }
                    setSelectedPlan(list[0]);
                }
            }
        } catch (error) {
            fireAlert({
                icon: 'error',
                title: 'Erro ao buscar planos',
                text: 'Ocorreu um erro ao buscar os planos. Por favor, tente novamente mais tarde.',
            })
        }
    }


    const applyTicket = async (value: string) => {
        if (!selectedPlan?.id_superlogica) {
            fireAlert({
                icon: 'error',
                title: 'Erro',
                text: 'Selecione um plano antes de aplicar o cupom.',
            })
            return;
        }

        setLoadingTicket(true);
        const res = await getTicket(value, selectedPlan.id_superlogica)
        setLoadingTicket(false);
        if (!res) {
            fireAlert({
                icon: 'error',
                title: 'Erro',
                text: 'Cupom inválido ou já utilizado.',
            })
            return;
        }

        setDiscountTicket(res);
    }

    useEffect(() => {
        fetchPlans();
    }, []);

    useEffect(() => {
        setDiscountTicket(null)
        if (cupom && selectedPlan) {
            applyTicket(cupom);
        }
    }, [selectedPlan]);

    useEffect(() => {
        if (!selectedPlan) return;

        //get discount value checking if percentage or value
        let discountValue = 0;
        if (discountTicket) {
            if (discountTicket.tipo_desconto === 'percentual') {
                discountValue = (selectedPlan.valor * discountTicket.desconto) / 100;
            } else {
                discountValue = discountTicket.desconto;
            }
        }

        const newInstallments = Array(selectedPlan.parcelas).fill(0).map((_, index) => ({
            value: `${index + 1}`,
            label: `${index + 1} x de ${toBrl((selectedPlan.valor - discountValue) / (index + 1))}`,
        }));
        setInstallmentsOptions(newInstallments)
        setSelectedInstallment(Number(newInstallments[newInstallments.length - 1].value));

    }, [selectedPlan, discountTicket]);

    return (
        <PlansContext.Provider value={{
            plans,
            paymentMethod,
            setPaymentMethod,
            selectedPlan,
            setSelectedPlan,
            installmentsOptions,
            selectedInstallment,
            setSelectedInstallment,
            discountTicket,
            setDiscountTicket,
            applyTicket,
            loadingTicket,
            allowPix,
            reativa,
            comercial
        }}>
            {children}
        </PlansContext.Provider>
    );
};

export const usePlans = () => {
    const ctx = useContext(PlansContext);
    if (!ctx) {
        throw new Error('usePlans must be used inside a <PlansProvider>');
    }
    return ctx;
};